/**
 * Campaign Summary Export — CSV row mapper.
 *
 * Spec: docs/AUDIT_PROMPT_CAMPAIGN_SUMMARY_EXPORT.md (canonical R2).
 *
 * CSV-injection guard is applied at the row mapper (Approach A from
 * the audit) so the existing exportToCSV utility stays untouched.
 * The guard targets operator-controlled cells only: Flight Name +
 * Variant. Numeric / date / status cells are not injection vectors.
 */

import type { CampaignSummaryRow } from "../services/campaigns.service";

// 2026-06-08: operator preference — render timestamps in Central
// (America/Chicago) instead of UTC. Operator team is HQ'd in Houston
// and reads the CSV alongside campaign send schedules that are
// already coordinated in Central. UTC was confusing.
const EXPORT_TIMEZONE = "America/Chicago";
const EXPORT_TIMEZONE_LABEL = "CT";

const HEADERS = [
  "Flight Name",
  "Status",
  "Variant",
  `Scheduled Send (${EXPORT_TIMEZONE_LABEL})`,
  `Sent At (${EXPORT_TIMEZONE_LABEL})`,
  "Recipients",
  "Sent",
  "Delivered",
  "Opened (unique)",
  "Clicked (unique)",
  "Bounced",
  "Unsubscribed",
  "Spam",
  "Pending",
  "Open Rate %",
  "Click Rate %",
] as const;

/**
 * Prefix the cell with a leading single-quote when it begins with a
 * character that Excel/Sheets would interpret as a formula
 * (`=`, `+`, `-`, `@`, tab, or CR). Applies only to operator-
 * controlled string cells.
 */
export function guardFormulaInjection(value: string): string {
  if (value.length === 0) {
    return value;
  }
  const first = value.charAt(0);
  if (
    first === "=" ||
    first === "+" ||
    first === "-" ||
    first === "@" ||
    first === "\t" ||
    first === "\r"
  ) {
    return `'${value}`;
  }
  return value;
}

/**
 * RFC 4180-style cell encoding: wrap in `"` and double inner `"` when
 * the cell contains a comma, quote, or newline.
 */
function csvEncode(value: string): string {
  if (
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Format a UTC ISO timestamp as Central Time using Intl.DateTimeFormat.
 * Produces "06/01/2026 10:00 AM CT". Empty string if the input is
 * null/invalid.
 */
function formatCentralOrEmpty(iso: string | null): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EXPORT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("month")}/${get("day")}/${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")} ${EXPORT_TIMEZONE_LABEL}`;
}

function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "0.00";
  }
  return Number(value).toFixed(2);
}

export function buildCampaignSummaryCsv(rows: CampaignSummaryRow[]): string {
  const lines: string[] = [];
  lines.push(HEADERS.join(","));

  for (const r of rows) {
    const cells = [
      // Operator-controlled — apply injection guard
      csvEncode(guardFormulaInjection(r.flight_name ?? "")),
      csvEncode(r.status ?? ""),
      // ab_test_variant is operator-named — guard too
      csvEncode(guardFormulaInjection(r.ab_test_variant ?? "")),
      csvEncode(formatCentralOrEmpty(r.scheduled_send_at)),
      csvEncode(formatCentralOrEmpty(r.sent_at)),
      String(r.recipients ?? 0),
      String(r.sent ?? 0),
      String(r.delivered ?? 0),
      String(r.opened_unique ?? 0),
      String(r.clicked_unique ?? 0),
      String(r.bounced ?? 0),
      String(r.unsubscribed ?? 0),
      String(r.spam ?? 0),
      String(r.pending ?? 0),
      formatRate(r.open_rate_pct),
      formatRate(r.click_rate_pct),
    ];
    lines.push(cells.join(","));
  }

  return lines.join("\n") + "\n";
}

/**
 * Slugify a campaign name into a filename-safe ASCII slug.
 * Lowercase + non-alphanumeric → dashes + collapse runs.
 * Trailing/leading dashes trimmed.
 */
export function slugifyForFilename(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize("NFKD")
      // Strip diacritics
      .replace(/[̀-ͯ]/g, "")
      // Non-alphanumeric → dash
      .replace(/[^a-z0-9]+/g, "-")
      // Collapse runs
      .replace(/-+/g, "-")
      // Trim edges
      .replace(/^-+|-+$/g, "") || "campaign"
  );
}

export function buildCampaignSummaryFilename(
  campaignName: string,
  now: Date = new Date(),
): string {
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(now.getUTCDate()).padStart(2, "0");
  return `campaign-summary-${slugifyForFilename(campaignName)}-${yyyy}-${mm}-${dd}.csv`;
}

/**
 * Trigger a browser download of the CSV. Uses Blob + ephemeral
 * anchor click — same pattern used elsewhere in the codebase for
 * direct downloads.
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
